import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { submitLead } from "@/lib/api";
import { SERVICES } from "@/lib/constants";

const initial = {
  full_name: "",
  email: "",
  phone: "",
  nationality: "",
  service: "Golden Visa",
  message: "",
};

export default function LeadForm({ compact = false, source = "website", testid = "lead-form" }) {
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setData((d) => ({ ...d, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!data.full_name || !data.email || !data.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({ ...data, source });
      setDone(true);
      setData(initial);
      toast.success("Thank you. An advisor will be in touch shortly.");
    } catch (err) {
      toast.error("Could not submit. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid={`${testid}-success`} className="border border-black/10 p-10 md:p-14">
        <div className="text-[11px] tracking-overline uppercase text-black/50">Received</div>
        <h3 className="font-serif text-4xl md:text-5xl mt-3 leading-none">Thank you.</h3>
        <p className="mt-5 text-black/65 max-w-md leading-relaxed">
          A senior advisor from Velor will contact you within one business day.
          For immediate assistance, message us on WhatsApp.
        </p>
        <button
          onClick={() => setDone(false)}
          data-testid={`${testid}-reset`}
          className="mt-8 text-[12px] tracking-overline uppercase border-b border-black pb-1 hover:opacity-60 transition"
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid={testid}
      className="border border-black/10 p-8 md:p-12 bg-white"
    >
      <div className="text-[11px] tracking-overline uppercase text-black/50">Private Enquiry</div>
      <h3 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">
        Speak with an advisor.
      </h3>
      <p className="text-black/55 mt-3 font-light max-w-md">
        Share a few details and we'll arrange a confidential consultation.
      </p>

      <div className={`mt-10 grid grid-cols-1 ${compact ? "" : "md:grid-cols-2"} gap-x-8 gap-y-2`}>
        <div>
          <label className="text-[11px] tracking-overline uppercase text-black/45">Full name</label>
          <input
            data-testid={`${testid}-fullname`}
            className="field-underline"
            name="full_name"
            value={data.full_name}
            onChange={onChange}
            placeholder="Jane Smith"
            required
          />
        </div>
        <div>
          <label className="text-[11px] tracking-overline uppercase text-black/45">Email</label>
          <input
            data-testid={`${testid}-email`}
            className="field-underline"
            name="email"
            type="email"
            value={data.email}
            onChange={onChange}
            placeholder="jane@email.com"
            required
          />
        </div>
        <div>
          <label className="text-[11px] tracking-overline uppercase text-black/45">Phone</label>
          <input
            data-testid={`${testid}-phone`}
            className="field-underline"
            name="phone"
            value={data.phone}
            onChange={onChange}
            placeholder="+44 7700 900000"
            required
          />
        </div>
        <div>
          <label className="text-[11px] tracking-overline uppercase text-black/45">Nationality</label>
          <input
            data-testid={`${testid}-nationality`}
            className="field-underline"
            name="nationality"
            value={data.nationality}
            onChange={onChange}
            placeholder="British"
          />
        </div>
        <div className={compact ? "" : "md:col-span-2"}>
          <label className="text-[11px] tracking-overline uppercase text-black/45">Service of interest</label>
          <select
            data-testid={`${testid}-service`}
            className="field-underline appearance-none"
            name="service"
            value={data.service}
            onChange={onChange}
          >
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.title}>{s.title}</option>
            ))}
            <option value="Other">Other</option>
          </select>
        </div>
        <div className={compact ? "" : "md:col-span-2"}>
          <label className="text-[11px] tracking-overline uppercase text-black/45">Message (optional)</label>
          <textarea
            data-testid={`${testid}-message`}
            className="field-underline resize-none"
            name="message"
            value={data.message}
            onChange={onChange}
            rows={3}
            placeholder="Tell us a little about your situation..."
          />
        </div>
      </div>

      <div className="mt-10 flex items-center justify-between gap-4">
        <p className="text-xs text-black/45 font-light max-w-xs">
          Your details are confidential and used only to assist your enquiry.
        </p>
        <button
          data-testid={`${testid}-submit`}
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-3 px-7 py-4 bg-black text-white text-[12px] tracking-overline uppercase hover:bg-black/85 transition-colors disabled:opacity-60"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
          Send Enquiry
        </button>
      </div>
    </form>
  );
}
